// @flow
import React, { Component, Fragment } from "react";
import { isEqual, noop } from "lodash";
import type { RaceTypeCodeEnum } from "@tvg/types/Race";
import type { UnaryFn } from "@tvg/types/Functional";
import formatSequencial from "@tvg/formatter/numeric";
import NumberFire from "@fdr/static-ui/SvgImages/NumberFire";
import RacingAndSports from "@fdr/static-ui/SvgImages/RacingAndSports";
import { type RunnerHandicapping } from "@fdr/types/ProgramPage";
import Icon from "@fdr/static-ui/Icons";
import buildColor from "@fdr/static-ui/ColorPalette";
import { arrowUp, arrowDown, numberFire } from "@fdr/static-ui/Icons/icons";

import RunnerNumber from "../../_atoms/RunnerNumber";

import {
  RacingTipOuterContainer,
  RacingTipContainer,
  RacingTipHeader,
  RacingTipAnalystVerdict,
  Tips,
  Tip,
  Spacer,
  Title,
  NumberFireLayout,
  BottomSection,
  AnyButton,
  ExpandButtonWrapper,
  ExpandButton
} from "./styled-components";

type Props = {
  runnersHandicapping: RunnerHandicapping[],
  timeformAnalystVerdict: string,
  onEventClick: UnaryFn<string, void>,
  raceType: RaceTypeCodeEnum,
  raceAnalystVerdictExpanded: boolean,
  setRaceAnalystVerdictExpanded: UnaryFn<boolean, void>,
  isSRW: boolean,
  isRAS: boolean,
  useNewProgramPageLayout: boolean
};

export default class RacingTip extends Component<Props> {
  static defaultProps = {
    onEventClick: noop,
    raceTipsData: [],
    raceType: "T",
    raceAnalystVerdictExpanded: false,
    setRaceAnalystVerdictExpanded: noop,
    isSRW: false,
    isRAS: false,
    useNewProgramPageLayout: false
  };

  shouldComponentUpdate = (nextProps: Props) => !isEqual(nextProps, this.props);

  raceAnalystVerdictExpandedHandler = () => {
    if (!this.props.timeformAnalystVerdict) return;
    if (!this.props.raceAnalystVerdictExpanded)
      this.props.onEventClick("RACING_TIP_MORE");

    this.props.setRaceAnalystVerdictExpanded(
      !this.props.raceAnalystVerdictExpanded
    );
  };

  renderRacingTips = (
    timeformAnalystVerdict: string = "",
    isNewLayout: boolean = false
  ) => {
    const isTipBelow: boolean = isNewLayout && !!timeformAnalystVerdict;
    const isTipHorizontal: boolean = isNewLayout && !timeformAnalystVerdict;

    return this.props.runnersHandicapping.map(
      (runner: RunnerHandicapping, length) => (
        <Fragment key={`tip_${runner.freePickNumber}`}>
          {isTipHorizontal && (
            <Tip isNewLayout>{formatSequencial(runner.freePickNumber)}</Tip>
          )}
          {isTipBelow && <Spacer />}
          {!isTipHorizontal && !isTipBelow && (
            <Tip>{formatSequencial(runner.freePickNumber)}</Tip>
          )}
          <RunnerNumber
            raceTypeCode={this.props.raceType}
            runnerId={runner.runnerId}
            hasTimeformInfo={false}
            isBigSize={false}
            isRightSpaced={isTipBelow && length === 2}
          />
        </Fragment>
      )
    );
  };

  render() {
    const {
      raceAnalystVerdictExpanded,
      timeformAnalystVerdict,
      isSRW,
      isRAS,
      useNewProgramPageLayout
    } = this.props;

    if (useNewProgramPageLayout) {
      return (
        <RacingTipOuterContainer>
          <RacingTipContainer
            raceAnalystVerdictExpanded={raceAnalystVerdictExpanded}
            timeformAnalystVerdict={timeformAnalystVerdict}
            onClick={this.raceAnalystVerdictExpandedHandler}
            isNewLayout
          >
            <RacingTipHeader timeformAnalystVerdict={timeformAnalystVerdict}>
              <Title isRAS={isRAS}>
                {isSRW ? (
                  "Free Prediction"
                ) : (
                  <Fragment>
                    {isRAS ? <RacingAndSports /> : <NumberFire />}
                    <NumberFireLayout>Predicts</NumberFireLayout>
                  </Fragment>
                )}
              </Title>
              <Tips>{this.renderRacingTips(timeformAnalystVerdict, true)}</Tips>
            </RacingTipHeader>
            {timeformAnalystVerdict && (
              <BottomSection>
                <ExpandButtonWrapper>
                  <ExpandButton>Race Analysis</ExpandButton>
                  {raceAnalystVerdictExpanded ? (
                    <Icon
                      icon={arrowUp}
                      size={17}
                      color={buildColor("blue", "500")}
                    />
                  ) : (
                    <Icon
                      icon={arrowDown}
                      size={17}
                      color={buildColor("blue", "500")}
                    />
                  )}
                </ExpandButtonWrapper>
                <div>
                  {this.props.runnersHandicapping.map(
                    (runner: RunnerHandicapping) => (
                      <Tip
                        isNewLayout
                        belowTheLine
                        key={`tip_${runner.freePickNumber}`}
                      >
                        {formatSequencial(runner.freePickNumber)}
                      </Tip>
                    )
                  )}
                </div>
              </BottomSection>
            )}
            {timeformAnalystVerdict && raceAnalystVerdictExpanded && (
              <RacingTipAnalystVerdict
                isNewLayout
                raceAnalystVerdictExpanded={raceAnalystVerdictExpanded}
              >
                {timeformAnalystVerdict}
              </RacingTipAnalystVerdict>
            )}
          </RacingTipContainer>
        </RacingTipOuterContainer>
      );
    }

    return (
      <RacingTipContainer
        raceAnalystVerdictExpanded={raceAnalystVerdictExpanded}
        timeformAnalystVerdict={timeformAnalystVerdict}
        onClick={this.raceAnalystVerdictExpandedHandler}
      >
        <RacingTipHeader timeformAnalystVerdict={timeformAnalystVerdict}>
          <Title>
            <NumberFire>
              {isSRW ? (
                "Free Prediction"
              ) : (
                <Fragment>
                  <Icon icon={numberFire} /> Predicts
                </Fragment>
              )}
            </NumberFire>
          </Title>
          <Tips>{this.renderRacingTips()}</Tips>
        </RacingTipHeader>
        {timeformAnalystVerdict && (
          <RacingTipAnalystVerdict
            raceAnalystVerdictExpanded={raceAnalystVerdictExpanded}
          >
            {timeformAnalystVerdict}
          </RacingTipAnalystVerdict>
        )}
        {timeformAnalystVerdict && !raceAnalystVerdictExpanded && (
          <AnyButton>More</AnyButton>
        )}
      </RacingTipContainer>
    );
  }
}
